/* eslint-disable no-console */
import { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import eventService from '../common/eventService';
import emitter from '../common/eventService/emitter';
import areBlocksEqual from '../common/areBlocksEqual';
import checkMissingMarkupValue from '../common/checkMissingMarkupValue';
import getLatestVersionHistoryItem from '../common/getLatestVersionHistoryItem';
import rules from '../constants/businessRules/businessRules';
import * as savingActions from '../actions/saving';
import * as versionHistoryActions from '../actions/versionHistory';
import * as htmlOutputActions from '../actions/htmlOutput';
import * as modalActions from '../actions/modals';
import { blockType } from '../types';
import { request } from '../common/request';
import configs from '../constants/configs';
import {
    ERR_SAVE_SEND_DATA_TO_INTEGRATIONS,
    ERR_VERSION_HISTORY_SAVE,
    ERR_VERSION_HISTORY_UNDEFINED_BLOCKS,
} from '../constants/Errors';
import {
    enableExitWarning,
    disableExitWarning,
    resetAutoSaveTimer,
    startAutoSaveTrigger,
} from '../middleware/autoSave';

/**
 * This function sends save information as message so integrations can save it
 * @param {object} blocks - All the blocks that are added to the canvas
 * @param {function} dispatch - Redux action dispatcher
 */
const _sendSaveDataToIntegrations = (blocks, dispatch, { html, htmlWithEmbeddedCustomFonts }, pendingSaveId) => {
    eventService.sendHtml(html, 'save', JSON.stringify(blocks), pendingSaveId);
    if (html?.length >= rules.HTML_MAX_CONTENT_SIZE) {
        dispatch(modalActions.setModal({ name: 'htmlSizeWarningModal', status: true, location: null }));
    }
    dispatch(htmlOutputActions.updatedHtmlBody({ html, useEmbeddedCustomFonts: true, htmlWithEmbeddedCustomFonts }));
};

const _sendSaveDataToIntegrationsOld = async (draftID, blocks, dispatch, pendingSaveId) => {
    try {
        const { html, htmlWithEmbeddedCustomFonts } = await request.convertHtml({
            draftID,
            blocks,
            useEmbeddedCustomFonts: true,
        });
        dispatch(
            htmlOutputActions.updatedHtmlBody({ html, useEmbeddedCustomFonts: true, htmlWithEmbeddedCustomFonts })
        );
        eventService.sendHtml(html, 'save', JSON.stringify(blocks), pendingSaveId);
        if (html?.length >= rules.HTML_MAX_CONTENT_SIZE) {
            dispatch(modalActions.setModal({ name: 'htmlSizeWarningModal', status: true, location: null }));
        }
    } catch (err) {
        console.error(err);
        eventService.notify(err.customMessage ? err.customMessage : ERR_SAVE_SEND_DATA_TO_INTEGRATIONS, true);
    }
};

const newPendingSaveId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const saveVersionHistoryItem = async (
    pendingSaveId,
    blocks,
    isVersionHistoryItemSaving,
    documentID,
    documentType,
    integration,
    dispatch,
    feed3220CheckForUndefinedBlocks
) => {
    // ${d:t}${draftID}${versionID}${trackID}
    if (blocks.length === 0) {
        dispatch(versionHistoryActions.trackVersionHistoryPendingSave(pendingSaveId));
        dispatch(savingActions.updateSavingStatus(false));
        return null;
    }

    const latestItem = await getLatestVersionHistoryItem(documentID);
    if ((!_isEmpty(latestItem) && areBlocksEqual(blocks, latestItem.blocks)) || isVersionHistoryItemSaving) {
        // In the case the blocks are equal, the integrations still expect a save message back
        if (!isVersionHistoryItemSaving) {
            // A retry after a rejected save has no new item to write; track the stored one instead.
            dispatch(versionHistoryActions.setVersionHistoryPendingSavedItem({ ...latestItem, pendingSaveId }));
        }
        dispatch(savingActions.updateSavingStatus(false));
        return null;
    }

    // Check to see if any of the blocks are undefined, this means something broke, we don't want to allow saving and refresh the page.
    if (feed3220CheckForUndefinedBlocks && checkMissingMarkupValue(blocks)) {
        dispatch(savingActions.updateSavingStatus(false));
        console.error('Unable to save due to broken text blocks. Refreshing email designer.');

        // refresh page to get rid of the cke undefined text block
        disableExitWarning();
        window.location.reload(true);
        enableExitWarning();

        return new Error(ERR_VERSION_HISTORY_UNDEFINED_BLOCKS);
    }

    dispatch(versionHistoryActions.updateVersionHistoryItemSavingStatus({ isSaving: true }));
    dispatch(savingActions.updateSavingStatus(false));

    try {
        const item = await request.post(configs.versionHistoryItem, {
            blocks: JSON.stringify(blocks),
            documentID,
            type: documentType,
            integration,
        });
        dispatch(
            versionHistoryActions.setVersionHistoryPendingSavedItem({ ...item, blocks, pendingSaveId })
        );
        return null;
    } catch (err) {
        // TODO: add proper internal logging
        startAutoSaveTrigger();
        resetAutoSaveTimer();
        return new Error(err.customMessage ? err.customMessage : ERR_VERSION_HISTORY_SAVE);
    }
};

const onSave = async (
    blocks,
    isVersionHistoryItemSaving,
    documentID,
    draftID,
    documentType,
    integration,
    dispatch,
    feed3220CheckForUndefinedBlocks,
    ed925MoveConvertBeforeSave
) => {
    if (!documentID || isVersionHistoryItemSaving) {
        dispatch(savingActions.updateSavingStatus(false));
        return;
    }

    let html;
    let htmlWithEmbeddedCustomFonts;

    if (ed925MoveConvertBeforeSave) {
        try {
            ({ html, htmlWithEmbeddedCustomFonts } = await request.convertHtml({
                draftID,
                blocks,
                useEmbeddedCustomFonts: true,
            }));
        } catch (err) {
            console.error(err);
            eventService.notify(err.customMessage || ERR_SAVE_SEND_DATA_TO_INTEGRATIONS, true);
            return;
        }
    }

    // Sent with the save and echoed back by the integration, so replies match this save.
    const pendingSaveId = newPendingSaveId();
    const saveVersionHistoryItemReturn = await saveVersionHistoryItem(
        pendingSaveId,
        blocks,
        isVersionHistoryItemSaving,
        documentID,
        documentType,
        integration,
        dispatch,
        feed3220CheckForUndefinedBlocks
    );

    if (saveVersionHistoryItemReturn) {
        eventService.notify(saveVersionHistoryItemReturn, true);
        dispatch(versionHistoryActions.updateVersionHistoryItemSavingStatus({ isSaving: false }));
        return;
    }

    if (ed925MoveConvertBeforeSave) {
        await _sendSaveDataToIntegrations(blocks, dispatch, { html, htmlWithEmbeddedCustomFonts }, pendingSaveId);
    } else {
        await _sendSaveDataToIntegrationsOld(draftID, blocks, dispatch, pendingSaveId);
    }
};

const Save = ({
    clientIP,
    userEmail,
    hasSocketAutoSaveEnabled,
    hasVersionHistoryAccess,
    saving,
    blocks,
    isVersionHistoryItemSaving,
    documentID,
    draftID,
    documentType,
    integration,
    dispatch,
    flags,
    ldClient,
}) => {
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
    const { feed3220CheckForUndefinedBlocks, ed925MoveConvertBeforeSave } = flags;

    const blocksRef = useRef(blocks);
    blocksRef.current = blocks;

    useEffect(() => {
        const setTriggerSaveListener = () => {
            dispatch(savingActions.updateSavingStatus(true));
        };

        const setTriggerAutoSaveListener = () => {
            const feed4488DisableSaveForEmptyMarkup = ldClient.variation(
                'FEED-4488-disable-save-for-empty-markup',
                false
            );

            if (feed4488DisableSaveForEmptyMarkup) {
                if (checkMissingMarkupValue(blocksRef.current, true)) {
                    console.error('Unable to auto-save due to empty text blocks. Please refresh the page.');
                    return;
                }
            }

            dispatch(savingActions.updateSavingStatus(true));
        };

        if (emitter.listenerCount(rules.EVENT_EMITTER_EVENT_TRIGGERS.TRIGGER_SAVE) === 0) {
            emitter.on(rules.EVENT_EMITTER_EVENT_TRIGGERS.TRIGGER_SAVE, setTriggerSaveListener);
        }

        if (
            autoSaveEnabled &&
            hasSocketAutoSaveEnabled &&
            emitter.listenerCount(rules.EVENT_EMITTER_EVENT_TRIGGERS.TRIGGER_AUTO_SAVE) === 0
        ) {
            emitter.on(rules.EVENT_EMITTER_EVENT_TRIGGERS.TRIGGER_AUTO_SAVE, setTriggerAutoSaveListener);
        }
    }, [autoSaveEnabled, hasSocketAutoSaveEnabled, dispatch]);

    useEffect(() => {
        // ${d:t}${draftID}${versionID}${trackID}
        if (hasVersionHistoryAccess && documentID.length >= 19) {
            const updateVersionHistoryLatestSavedItem = async () => {
                const latestItem = await getLatestVersionHistoryItem(documentID);
                if (!_isEmpty(latestItem)) {
                    dispatch(versionHistoryActions.updateVersionHistoryLatestSavedItem(latestItem));
                }
            };

            updateVersionHistoryLatestSavedItem();
        }
    }, [documentID, hasVersionHistoryAccess, dispatch]);

    useEffect(() => {
        if (!clientIP || !userEmail) {
            setAutoSaveEnabled(false);
            return;
        }

        // If anyone is inside staffbase offices or using VPN logged in as a non-bananatag/non-staffbase email, the auto save will be disabled.
        if (
            rules.BANANATAG_OFFICE_IPS.includes(clientIP) &&
            !userEmail.includes('@bananatag') &&
            !userEmail.includes('@staffbase')
        ) {
            setAutoSaveEnabled(false);
            return;
        }

        setAutoSaveEnabled(true);
    }, [clientIP, userEmail]);

    if (saving && !isVersionHistoryItemSaving) {
        onSave(
            blocks,
            isVersionHistoryItemSaving,
            documentID,
            draftID,
            documentType,
            integration,
            dispatch,
            feed3220CheckForUndefinedBlocks,
            ed925MoveConvertBeforeSave
        ).catch(console.error);
    }

    return null;
};

const mapStateToProps = (state) => {
    let hasSocketAutoSaveEnabled = _get(state, 'session.features.F_SAVE', false);
    const connectedClients = _get(state, 'session.collaboration.connectedClients', []);
    const currentUserSocketId = _get(state, 'session.collaboration.currentUserSocketId', '');
    const currentUserIndex = connectedClients.findIndex((client) => client.socketId === currentUserSocketId);

    if (currentUserIndex >= 0) {
        hasSocketAutoSaveEnabled = connectedClients[currentUserIndex].hasAutoSaveEnabled;
    }
    return {
        documentID:
            (_get(state, 'session.collaboration.type', '') === 'template' ? 't' : 'd') +
            _get(state, 'session.collaboration.id', '') +
            _get(state, 'session.collaboration.versionID', '') +
            _get(state, 'session.collaboration.trackID', ''),
        draftID: _get(state, 'session.collaboration.id', ''),
        documentType: _get(state, 'session.collaboration.type', 'draft'),
        isVersionHistoryItemSaving: _get(state, 'versionHistory.isVersionHistoryItemSaving', false),
        integration: _get(state, 'session.integration', ''),
        blocks: _get(state, 'canvasBlocks.present', []),
        clientIP: _get(state, 'session.user.ipAddress', ''),
        userEmail: _get(state, 'session.user.email', ''),
        saving: state.saving,
        hasSocketAutoSaveEnabled,
        hasVersionHistoryAccess:
            (_get(state, 'session.user.plan', '') === 'IC_ENTERPRISE' ||
                _get(state, 'session.user.plan', '') === 'IC_SBENTERPRISE') &&
            _get(state, 'session.features.F_VERSION_HISTORY', false),
    };
};

Save.propTypes = {
    documentID: PropTypes.string.isRequired,
    draftID: PropTypes.string.isRequired,
    documentType: PropTypes.string.isRequired,
    isVersionHistoryItemSaving: PropTypes.bool.isRequired,
    blocks: PropTypes.arrayOf(blockType).isRequired,
    integration: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    clientIP: PropTypes.string.isRequired,
    flags: PropTypes.shape({
        feed3220CheckForUndefinedBlocks: PropTypes.bool.isRequired,
        ed925MoveConvertBeforeSave: PropTypes.bool.isRequired,
        feed4488DisableSaveForEmptyMarkup: PropTypes.bool.isRequired,
    }).isRequired,
    ldClient: PropTypes.shape({
        variation: PropTypes.func,
    }).isRequired,
    userEmail: PropTypes.string.isRequired,
    saving: PropTypes.bool.isRequired,
    hasSocketAutoSaveEnabled: PropTypes.bool.isRequired,
    hasVersionHistoryAccess: PropTypes.bool.isRequired,
};

export default withLDConsumer()(connect(mapStateToProps)(Save));
